import { Component } from "@angular/core";
import { IUserBankAccount } from "@vierkant-software/types__api";
import { DraftForm, DraftFormType, DraftService, FC_A, OnResolve } from "src/app/modules/ngx-draft";
import { IFrontendUser } from "../../contract.interfaces";
import { DraftOptions } from "src/app/components/v-overview/v-overview.component";
import { formatUser } from "src/util/formatting/user";
import { formatIbanShort } from "src/util/formatting/bankAccount";
import { UserStepperDefinition } from "../../user.stepperdefinition";

@Component({
    templateUrl: './bank-data.step.haml',
    styleUrls:   ['./bank-data.step.sass'],
    standalone:  false
})
export class UserBankDataStep implements OnResolve {
    draft: DraftOptions = {
        baseURI: "bank-account",
        anchor:  "bank-data",
        options: {
            openAsModal:  true,
            showAllSteps: true,
        }
    };

    @DraftForm() form: DraftFormType<IFrontendUser>;

    public get bankAccountList(){
        return this.form.get(['bankAccountList']) as FC_A<IUserBankAccount>;
    }

    /** online angegebene Bankverbindung, wenn der Step aus dem Ticket "Onlinevertrag angelegt" geoeffnet wurde */
    private get ticketData(): IFrontendUser['ticketData'] {
        return this.form.value.ticketData;
    }

    public get ticketIBAN(): string | undefined {
        return this.ticketData?.IBAN;
    }

    constructor(
        public draftService: DraftService,
    ){}

    onResolve() {
        this.addTicketBankAccount();
    }

    /**
     * Called on resolve. If the step was opened from the "member contract online created" ticket, adds the
     * online bank account (IBAN, BIC, account holder) as a new, unsaved and active account.
     * - Skipped if an active account with the same IBAN already exists. This includes the account
     *   added before a draft reload, so it is never added twice.
     * - An inactive account with the same IBAN is not reactivated. A new active one is added instead.
     * - Without an online account holder, the person's name is used, as when adding an account manually.
     */
    private addTicketBankAccount() {
        const iban = this.ticketIBAN;
        if (!iban) return;
        const { BIC, accountOwner } = this.ticketData;
        const ibanShort = formatIbanShort(iban);
        const exists = this.bankAccountList.value.some(account =>
            account.isActive && (account.iban === iban || account.ibanShort === ibanShort));
        if (exists) return;
        UserStepperDefinition.addBankAccount(this.form, {
            ID:                  undefined,
            isActive:            true,
            iban,
            bic:                 BIC || undefined,
            accountQwner:        accountOwner || formatUser(this.form.value, 'normal'),
            defaultForContracts: false,
            defaultForSales:     false,
            ibanShort,
        });
    }

    onStandardChanged(index: number, value: boolean){
        this.bankAccountList.controls.forEach((x, i) => {
            x.setValue({
                ...x.value,
                defaultForContracts: index === i ? value : false,
            }, {emitEvent: false});
        });
        this.bankAccountList.updateValueAndValidity();
    }

    onSalesChanged(index: number, value: boolean){
        this.bankAccountList.controls.forEach((x, i) => {
            x.setValue({
                ...x.value,
                defaultForSales: index === i ? value : false,
            }, {emitEvent: false});
        });
        this.bankAccountList.updateValueAndValidity();
    }

    async createBankData(){
        return await this.draftService.createDraft(this.draft.baseURI, this.draft.anchor, {
            ...this.draft.options,
            data: {
                accountQwner: formatUser(this.form.value, 'normal'),
            },
            chain: true});
    }
}
